/**
 * AcceptInvitePage (`/accept-invite`) — landing page for invite emails.
 *
 * Two-step flow:
 *
 *   1. On mount we call `GET /auth/invite-info?token=…` which validates
 *      the token but does NOT consume it. Idempotent and safe to call
 *      multiple times — React 18 StrictMode dev double-invokes effects,
 *      hot reload re-mounts components, the user might refresh, etc.
 *      Burning a single-use token in any of those paths would surface
 *      a confusing "invalid or expired" error to a user who clicked
 *      exactly once.
 *
 *   2. We render a confirmation card with the workspace name and the
 *      email being invited. If the user is currently signed in as a
 *      DIFFERENT account, we surface that prominently so they can't
 *      accidentally swap sessions. Only when the user clicks
 *      "Accept invite" do we POST `/auth/accept-invite`, which is
 *      the destructive call that actually swaps the JWT and burns
 *      the token.
 *
 * Errors at either step share a single error UI with affordances back
 * to the regular login flows.
 */
import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Button,
  Alert,
  Divider,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import OnboardingLayout from "./onboarding/components/OnboardingLayout";
import { buildApiUrl } from "../config";
import { apiCache } from "../utils/apiCache";
import { setAuth } from "../utils/auth";
import { identifyUser, trackUserLoggedIn } from "../utils/analytics";

type InviteInfo = {
  invitee_email: string;
  invitee_name: string;
  workspace_name: string;
  inviter_name?: string | null;
  expires_at: string;
};

type Phase = "loading" | "preview" | "accepting" | "error" | "used";

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [phase, setPhase] = React.useState<Phase>("loading");
  const [info, setInfo] = React.useState<InviteInfo | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [currentEmail, setCurrentEmail] = React.useState<string | null>(null);

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem("klaris_user");
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.email) setCurrentEmail(String(u.email).toLowerCase());
      }
    } catch {
      // ignore — we treat absence as "not signed in"
    }
  }, []);

  React.useEffect(() => {
    if (!token) {
      setError("This invite link is missing its token. Ask your admin to resend the invite.");
      setPhase("error");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(buildApiUrl("/api/v1/auth/invite-info"), {
          params: { token },
        });
        if (cancelled) return;
        setInfo(res.data as InviteInfo);
        setPhase("preview");
      } catch (err: unknown) {
        if (cancelled) return;
        const e = err as { response?: { status?: number; data?: { detail?: string } } };
        // Invite links do not expire, so "not found" is a link already used:
        // someone who joined before and opened the invite email again to get
        // back in. (A link replaced by a newer invite reads the same; that
        // person has the newer email.)
        if (e?.response?.status === 404 && e.response.data?.detail !== "Workspace no longer exists.") {
          setPhase("used");
          return;
        }
        setError(
          e?.response?.data?.detail ||
            "We could not check this invite link. Please try again in a moment.",
        );
        setPhase("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAccept = async () => {
    if (!info) return;
    setPhase("accepting");
    setError(null);
    try {
      const res = await axios.post(buildApiUrl("/api/v1/auth/accept-invite"), { token });
      apiCache.clear();
      setAuth(res.data.access_token, res.data.user, res.data.tenant);

      try {
        identifyUser(res.data.user, res.data.tenant);
        trackUserLoggedIn();
      } catch {
        // Analytics failures must never block onboarding.
      }

      navigate("/", { replace: true });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(
        e?.response?.data?.detail ||
          "We couldn't accept this invite. Please ask your admin to send a fresh link.",
      );
      setPhase("error");
    }
  };

  const switchingAccounts =
    !!info && !!currentEmail && currentEmail !== info.invitee_email.toLowerCase();

  return (
    <OnboardingLayout maxWidth={460}>
      <Box
        sx={(t) => ({
          p: { xs: 3, sm: 4 },
          borderRadius: `${t.ds.radius.lg}px`,
          bgcolor: t.tokens.surface.raised,
          boxShadow: t.tokens.shadow.md,
          border: `1px solid ${t.tokens.border.subtle}`,
        })}
      >
        {phase === "loading" ? (
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={32} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Checking your invite…
            </Typography>
          </Stack>
        ) : phase === "used" ? (
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Looks like you have already accepted this invite
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in with your email and you will be taken straight to your
              workspace.
            </Typography>
            <Box sx={{ pt: 1 }}>
              <Button
                variant="contained"
                onClick={() => navigate("/login", { replace: true })}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Sign in with email code
              </Button>
            </Box>
          </Stack>
        ) : phase === "error" ? (
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              We couldn't accept this invite
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Typography variant="body2" color="text.secondary">
              You can sign in with an email code if your account already
              exists, or ask your administrator to send a fresh invite link.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                onClick={() => navigate("/login", { replace: true })}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Sign in with email code
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/login-pwd", { replace: true })}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Use password
              </Button>
            </Stack>
          </Stack>
        ) : (
          // preview / accepting
          <Stack spacing={2.5}>
            <Box>
              <Typography
                variant="overline"
                sx={(t) => ({
                  color: t.tokens.text.secondary,
                  letterSpacing: 0.6,
                  fontWeight: 600,
                })}
              >
                You've been invited
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                Join {info?.workspace_name}
              </Typography>
              {info?.inviter_name ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {info.inviter_name} invited you to collaborate.
                </Typography>
              ) : null}
            </Box>

            <Divider />

            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
                SIGNING IN AS
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                {info?.invitee_email}
              </Typography>
            </Stack>

            {switchingAccounts ? (
              <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
                You're currently signed in as <strong>{currentEmail}</strong>.
                Accepting this invite will switch you to{" "}
                <strong>{info?.invitee_email}</strong> on this device.
              </Alert>
            ) : null}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="contained"
                onClick={handleAccept}
                disabled={phase === "accepting"}
                sx={{ textTransform: "none", fontWeight: 600, flex: 1 }}
              >
                {phase === "accepting" ? (
                  <>
                    <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />
                    Accepting…
                  </>
                ) : switchingAccounts ? (
                  "Switch accounts & continue"
                ) : (
                  "Accept invite & continue"
                )}
              </Button>
              <Button
                variant="text"
                onClick={() => navigate("/login", { replace: true })}
                disabled={phase === "accepting"}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Not now
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </OnboardingLayout>
  );
}
